import { DeviceVaultOperation } from '../vault/device-vault/device-vault-operation';
import { DeviceVault } from '../vault/device-vault/device-vault';
import { getDeviceVaultEntryBytesToBeSigned } from '../vault/device-vault/device-vault-utils';
import { BoundedSemaphore } from '../utils/semaphore';
import { HttpStatusCodeError } from '../errors/http-status-code-error';
import { DevicePartialSuccessError } from '../errors/device-partial-success-error';
import { combineUint8Arrays, getUnsafeDateNow, sleep } from '../utils/utils';
export class DeviceService {
    constructor(cryptoService, deviceVaultRepository, uiFrameAdapter, logger, tweaks) {
        var _a;
        this.cryptoService = cryptoService;
        this.deviceVaultRepository = deviceVaultRepository;
        this.uiFrameAdapter = uiFrameAdapter;
        this.logger = logger;
        this.tweaks = tweaks;
        this.lastStartTimes = [];
        this.pool = new BoundedSemaphore((_a = tweaks.parallelism) !== null && _a !== void 0 ? _a : 20);
    }
    async addDevicesVaultEntry(deviceIds, groupId, operation, signingContext, vault, deviceGroupVault, deviceGroupPolicyVault) {
        var _a, _b, _c, _d;
        this.logger.logInformation(`Device Service: Batch operation ${DeviceVaultOperation[operation]} called with ${groupId} groupId for ${deviceIds.length} devices: ${deviceIds.join(', ')}}`);
        const batchSize = (_a = this.tweaks.batchSize) !== null && _a !== void 0 ? _a : 96;
        const waitAfterBatchInMs = (_b = this.tweaks.waitAfterBatchInMs) !== null && _b !== void 0 ? _b : 0;
        const maxStartedPerSecond = (_c = this.tweaks.maxStartedPerSecond) !== null && _c !== void 0 ? _c : 8;
        const clientSideRetryIntervals = (_d = this.tweaks.clientSideRetryIntervals) !== null && _d !== void 0 ? _d : [1000, 3000, 5000, 7000, 11000, 13000, 17000, 19000, 230000, 29000];
        this.logger.logInformation('Device Service: Starting batch operation with the following tweaks: ' + JSON.stringify({
            batchSize,
            waitAfterBatchInMs,
            parallelism: this.pool.upperBound,
            maxStartedPerSecond,
            clientSideRetryIntervals,
        }));
        const processedDeviceIds = [];
        const notProcessedDeviceIds = [];
        const partiallyProcessedDeviceIds = [];
        const resolvers = deviceIds.map(deviceId => async () => {
            const release = await this.pool.waitOne();
            this.lastStartTimes.splice(this.pool.upperBound); // Remove old unneeded entries
            const hasEnoughStartedInTheLastSecond = () => {
                const now = getUnsafeDateNow();
                const entriesFromLastSecond = this.lastStartTimes.filter(time => now.getTime() - time.getTime() < 1000);
                return entriesFromLastSecond.length >= maxStartedPerSecond;
            };
            while (hasEnoughStartedInTheLastSecond()) {
                await sleep(250 * Math.random());
            }
            this.lastStartTimes.unshift(getUnsafeDateNow());
            try {
                // get device vault
                const entries = await this.deviceVaultRepository.getDeviceVault(deviceId);
                // in case of initialization, if device is already initialized, move on
                if (entries.length > 0 && operation === DeviceVaultOperation.NoOp) {
                    return;
                }
                const deviceVault = await DeviceVault.Create(entries, vault, deviceGroupVault, deviceGroupPolicyVault, (operation === DeviceVaultOperation.NoOp && entries.length === 0));
                let entryId = entries.length;
                const deviceVaultEntries = [];
                let lastDeviceBlockChainSignature = null;
                if (entryId !== 0) {
                    lastDeviceBlockChainSignature = deviceVault.getLastEntry().blockchainSignature;
                    if (operation === DeviceVaultOperation.AddGroup && deviceVault.effectiveGroups.length !== 0) {
                        // Remove assigned group id in case of add operation.
                        const removeDeviceVaultEntry = await this.createDeviceVaultEntry(DeviceVaultOperation.RemoveGroup, entryId, deviceId, deviceVault.effectiveGroups[0].groupId, signingContext, lastDeviceBlockChainSignature);
                        this.logger.logInformation('Device Service: Remove device from group request');
                        lastDeviceBlockChainSignature = removeDeviceVaultEntry.blockchainSignature;
                        deviceVaultEntries.push(removeDeviceVaultEntry);
                        entryId++;
                    }
                }
                // compose device group entry
                const deviceVaultEntry = await this.createDeviceVaultEntry(operation, entryId, deviceId, groupId, signingContext, lastDeviceBlockChainSignature);
                deviceVaultEntries.push(deviceVaultEntry);
                // add device group
                this.logger.logInformation('Device Service: Add device vault entry');
                const httpClientOptions = {
                    retryIntervals: clientSideRetryIntervals,
                    onBeforeRetry: async () => {
                        while (hasEnoughStartedInTheLastSecond()) {
                            await sleep(250 * Math.random());
                        }
                        this.lastStartTimes.unshift(getUnsafeDateNow());
                    }
                };
                await this.deviceVaultRepository.addDeviceVaultEntry(deviceId, deviceVaultEntries, httpClientOptions);
                processedDeviceIds.push(deviceId);
            }
            catch (ex) {
                this.logger.logError(`Error during processing of deviceId. Error ${ex}`);
                if (ex instanceof HttpStatusCodeError && ex.status === 207) {
                    partiallyProcessedDeviceIds.push(deviceId);
                }
                else {
                    notProcessedDeviceIds.push(deviceId);
                }
                throw ex;
            }
            finally {
                release();
            }
        });
        let promises = [];
        for (let i = 0; i < resolvers.length; i += batchSize) {
            if (i !== 0) {
                await sleep(waitAfterBatchInMs);
            }
            const batch = resolvers.slice(i, i + batchSize);
            const promisesOfBatch = batch.map(resolver => resolver());
            promises = [...promises, ...promisesOfBatch];
            try {
                await Promise.allSettled(promisesOfBatch);
            }
            catch (_e) {
                // handled at the end for all promises
            }
        }
        const results = await Promise.allSettled(promises);
        if (results.some(result => result.status === 'rejected')) {
            processedDeviceIds.sort();
            notProcessedDeviceIds.sort();
            partiallyProcessedDeviceIds.sort();
            this.logger.logError(`Device Service: Batch operation ${DeviceVaultOperation[operation]} with ${groupId} groupId processed device ids: ${processedDeviceIds}, not processed device ids: ${notProcessedDeviceIds}, partially processed device ids ${partiallyProcessedDeviceIds}.`);
            throw new DevicePartialSuccessError(processedDeviceIds, notProcessedDeviceIds, partiallyProcessedDeviceIds);
        }
        else {
            this.logger.logInformation(`Device Service: Batch operation ${DeviceVaultOperation[operation]} successfully completed with ${groupId} groupId for ${deviceIds.length} devices: ${deviceIds.join(', ')}}`);
        }
    }
    async createDeviceVaultEntry(operation, entryId, deviceId, groupId, signingContext, lastEntryblockChainSignature) {
        const newEntry = {
            entryId,
            deviceId,
            companyId: this.uiFrameAdapter.companyId,
            groupId,
            policyId: null,
            policyType: null,
            policyHash: null,
            addedAt: signingContext.timeSource.now(),
            userId: this.uiFrameAdapter.userId,
            vaultEntryId: signingContext.signerEntry.id,
            operation,
            version: 1,
        };
        // sign: entry signature
        const entrySignature = new Uint8Array(await this.cryptoService.sign(signingContext.keyPair.privateKey, getDeviceVaultEntryBytesToBeSigned(newEntry)));
        // sign: blockchain signature
        let blockchainSignature;
        if (entryId !== 0 && lastEntryblockChainSignature) {
            const blockChainSignaturePayload = combineUint8Arrays(lastEntryblockChainSignature, entrySignature);
            blockchainSignature = new Uint8Array(await this.cryptoService.sign(signingContext.keyPair.privateKey, blockChainSignaturePayload));
        }
        else {
            blockchainSignature = entrySignature;
        }
        return Object.assign({ entrySignature, blockchainSignature }, newEntry);
    }
}
